import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { folderDisplayTitle } from '../utils/folderDisplay';
import './PipelineDashboard.css';

const POLL_MS = 20000;

const QUEUE_RANK = {
  processing: 0,
  in_queue: 1,
  paused: 2,
  not_started: 3,
  completed: 4
};

const QUEUE_LABEL = {
  in_queue: 'In Queue',
  processing: 'Processing',
  paused: 'Paused',
  completed: 'Completed',
  not_started: 'Not started'
};

const REVIEW_LABEL = {
  pending: 'Pending Review',
  reviewed: 'Reviewed'
};

const MEDIUM_LABEL = {
  pending: 'Pending',
  english: 'English',
  urdu: 'Urdu',
  urog: 'UrOG'
};

const EDU_LABEL = {
  pending: 'Pending',
  ready: 'Ready for EduPage',
  synced: 'Synced to EduPage'
};

const PRINT_LABEL = {
  ready: 'Ready to Print',
  printed: 'Printed'
};

const TASK_LABEL = {
  reviewed: 'Marked reviewed',
  check: 'Check started',
  regrade: 'Regrade',
  finished: 'Check finished',
  printed: 'Printed'
};

const TASK_FILTERS = [
  { id: '', label: 'All' },
  { id: 'check', label: 'Check started' },
  { id: 'regrade', label: 'Regrade' },
  { id: 'reviewed', label: 'Marked reviewed' },
  { id: 'finished', label: 'Check finished' },
  { id: 'printed', label: 'Printed' }
];

const FLAG_LABEL = {
  upload_mismatch: 'Local ≠ web',
  odd_count: 'Odd count',
  check_failed: 'Check failed',
  ocr_missing: 'OCR missing',
  grand_total: 'Total mismatch'
};

function timeMs(value) {
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

function RegradeTime({ run }) {
  const [tip, setTip] = useState(null);
  const names = Array.isArray(run.students) ? run.students.filter(Boolean) : [];
  const place = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - 300);
    setTip({ top: rect.bottom + 6, left: Math.max(8, left) });
  };
  return (
    <span
      className="pd-regrade-time"
      onMouseEnter={place}
      onMouseLeave={() => setTip(null)}
      onFocus={place}
      onBlur={() => setTip(null)}
      tabIndex={0}
    >
      {formatWhen(run.at)}
      {tip && createPortal(
        <span className="pd-regrade-tip" style={{ top: tip.top, left: tip.left }} role="tooltip">
          {names.length
            ? names.map((name) => <span key={name}>{name}</span>)
            : <span>Student list was not saved</span>}
        </span>,
        document.body
      )}
    </span>
  );
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function localDayKey(value) {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function tasksFromRows(rows) {
  const tasks = [];
  for (const row of rows) {
    const folder = titleOf(row);
    const push = (at, kind, user) => {
      const day = localDayKey(at);
      if (!day) return;
      tasks.push({
        key: `${row.id}-${kind}-${at}`,
        at,
        day,
        kind,
        user: String(user || '').trim(),
        folder,
        row
      });
    };
    push(row.reviewed_at, 'reviewed', row.reviewed_by);
    push(row.check_started_at, 'check', row.check_started_by);
    (Array.isArray(row.regrades) ? row.regrades : []).forEach((run, index) => {
      const day = localDayKey(run && run.at);
      if (!day) return;
      tasks.push({
        key: `${row.id}-regrade-${index}-${run.at}`,
        at: run.at,
        day,
        kind: 'regrade',
        user: String((run && run.by) || '').trim(),
        folder,
        row
      });
    });
    push(row.completed_at, 'finished', row.finished_by);
    if (row.print === 'printed') push(row.print_at, 'printed', row.printed_by);
  }
  tasks.sort((a, b) => timeMs(a.at) - timeMs(b.at));
  return tasks;
}

function dateKey(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDateKey(key) {
  if (!key) return '—';
  const [year, month, day] = key.split('-').map(Number);
  if (!year || !month || !day) return '—';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function monthCells(year, month) {
  const firstWeekday = new Date(year, month, 1).getDay();
  const start = new Date(year, month, 1 - firstWeekday);
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    cells.push({
      key: dateKey(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month
    });
  }
  return cells;
}

function kpisOf(list) {
  let evaluating = 0;
  let ready = 0;
  let archived = 0;
  for (const row of list) {
    if (row.queue === 'processing' || row.queue === 'in_queue') evaluating += 1;
    if (row.edupage === 'ready') ready += 1;
    if (row.print === 'printed' || row.edupage === 'synced') archived += 1;
  }
  return {
    total_batches: list.length,
    currently_evaluating: evaluating,
    ready_for_edupage: ready,
    printed_archived: archived
  };
}

function titleOf(row) {
  return folderDisplayTitle(row) || row.folder_path || `Folder ${row.id}`;
}

export default function PipelineDashboard() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('live');
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [review, setReview] = useState('all');
  const [medium, setMedium] = useState('all');
  const [queue, setQueue] = useState('all');
  const [edupage, setEdupage] = useState('all');
  const [sort, setSort] = useState({ key: 'queue', dir: 'asc' });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [taskMonth, setTaskMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [taskDay, setTaskDay] = useState(() => dateKey(new Date()));
  const [taskEvent, setTaskEvent] = useState('');
  const [taskQuery, setTaskQuery] = useState('');
  const [taskSort, setTaskSort] = useState({ key: 'time', dir: 'asc' });
  const [showTasks, setShowTasks] = useState(false);

  const load = useCallback(async (nextScope, { silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${window.API_BASE_URL}/analyze/pipeline-summary?scope=${nextScope}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || `Could not load the pipeline (${response.status})`);
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setGeneratedAt(data.generated_at || new Date().toISOString());
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load the pipeline');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(scope);
  }, [load, scope]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      load(scope, { silent: true });
    };
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load(scope, { silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, scope]);

  const todayKey = dateKey(new Date());

  const countsByDate = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const key = dateKey(row.test_date);
      if (!key) continue;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [rows]);

  const dateRows = useMemo(() => {
    if (!rangeStart) return rows;
    const end = rangeEnd || rangeStart;
    const lo = rangeStart < end ? rangeStart : end;
    const hi = rangeStart < end ? end : rangeStart;
    return rows.filter((row) => {
      const key = dateKey(row.test_date);
      return key && key >= lo && key <= hi;
    });
  }, [rows, rangeStart, rangeEnd]);

  const viewKpis = useMemo(() => kpisOf(dateRows), [dateRows]);

  const handleDateClick = (key) => {
    if (!rangeStart || rangeEnd) {
      setRangeStart(key);
      setRangeEnd(null);
      return;
    }
    if (key === rangeStart) return;
    if (key < rangeStart) {
      setRangeEnd(rangeStart);
      setRangeStart(key);
      return;
    }
    setRangeEnd(key);
  };

  const tasks = useMemo(() => tasksFromRows(rows), [rows]);
  const taskCounts = useMemo(() => {
    const map = new Map();
    for (const task of tasks) map.set(task.day, (map.get(task.day) || 0) + 1);
    return map;
  }, [tasks]);
  const dayTasks = useMemo(
    () => tasks.filter((task) => task.day === taskDay),
    [tasks, taskDay]
  );
  const shownTasks = useMemo(() => {
    const needle = taskQuery.trim().toLowerCase();
    const next = dayTasks.filter((task) => {
      if (taskEvent && task.kind !== taskEvent) return false;
      if (!needle) return true;
      return `${task.user || ''} ${TASK_LABEL[task.kind] || ''} ${task.folder}`.toLowerCase().includes(needle);
    });
    const dir = taskSort.dir === 'desc' ? -1 : 1;
    next.sort((a, b) => {
      let diff = 0;
      if (taskSort.key === 'event') {
        diff = (TASK_LABEL[a.kind] || '').localeCompare(TASK_LABEL[b.kind] || '');
      } else if (taskSort.key === 'user') {
        diff = (a.user || '').localeCompare(b.user || '', undefined, { sensitivity: 'base' });
      } else if (taskSort.key === 'folder') {
        diff = a.folder.localeCompare(b.folder, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        diff = timeMs(a.at) - timeMs(b.at);
      }
      if (diff === 0) diff = timeMs(a.at) - timeMs(b.at);
      return diff * dir;
    });
    return next;
  }, [dayTasks, taskEvent, taskQuery, taskSort]);

  const shiftMonth = (delta) => {
    setCalendarMonth((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const shiftTaskMonth = (delta) => {
    setTaskMonth((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const next = dateRows.filter((row) => {
      if (review !== 'all' && row.review !== review) return false;
      if (medium !== 'all' && row.medium !== medium) return false;
      if (queue !== 'all' && row.queue !== queue) return false;
      if (edupage !== 'all' && row.edupage !== edupage) return false;
      if (!query) return true;
      const hay = [
        titleOf(row),
        row.class,
        row.subject,
        row.assignment_name,
        row.created_by
      ].join(' ').toLowerCase();
      return hay.includes(query);
    });
    const dir = sort.dir === 'desc' ? -1 : 1;
    next.sort((a, b) => {
      let diff = 0;
      if (sort.key === 'name') {
        diff = titleOf(a).localeCompare(titleOf(b), undefined, { numeric: true, sensitivity: 'base' });
      } else if (sort.key === 'assignment') {
        diff = String(a.assignment_name || '').localeCompare(String(b.assignment_name || ''), undefined, { numeric: true, sensitivity: 'base' });
      } else if (sort.key === 'date') {
        diff = dateKey(a.test_date).localeCompare(dateKey(b.test_date));
      } else if (sort.key === 'review') {
        diff = (REVIEW_LABEL[a.review] || '').localeCompare(REVIEW_LABEL[b.review] || '');
      } else if (sort.key === 'medium') {
        diff = (MEDIUM_LABEL[a.medium] || '').localeCompare(MEDIUM_LABEL[b.medium] || '');
      } else if (sort.key === 'images') {
        diff = (a.scan_count || 0) - (b.scan_count || 0);
      } else if (sort.key === 'progress') {
        diff = (a.progress_pct || 0) - (b.progress_pct || 0);
      } else if (sort.key === 'check_started') {
        diff = timeMs(a.check_started_at) - timeMs(b.check_started_at);
      } else if (sort.key === 'regrade') {
        const latest = (row) => (Array.isArray(row.regrades) ? row.regrades : [])
          .reduce((max, run) => Math.max(max, timeMs(run.at)), 0);
        diff = latest(a) - latest(b);
      } else {
        diff = (QUEUE_RANK[a.queue] ?? 9) - (QUEUE_RANK[b.queue] ?? 9);
        if (diff === 0) {
          diff = titleOf(a).localeCompare(titleOf(b), undefined, { numeric: true, sensitivity: 'base' });
        }
      }
      return diff * dir;
    });
    return next;
  }, [dateRows, search, review, medium, queue, edupage, sort]);

  const toggleTaskSort = (key) => {
    setTaskSort((current) => {
      if (current.key !== key) return { key, dir: 'asc' };
      return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const taskSortMark = (key) => {
    if (taskSort.key !== key) return '';
    return taskSort.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleSort = (key) => {
    setSort((current) => {
      if (current.key !== key) return { key, dir: 'asc' };
      return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const openCheck = (row) => {
    navigate('/evaluate/check', {
      state: {
        autoOpenFolder: {
          folder_path: row.folder_path,
          title: titleOf(row),
          class: row.class || '',
          subject: row.subject || ''
        }
      }
    });
  };

  const openScans = (row) => {
    navigate('/scans', { state: { autoOpenFolderId: row.id } });
  };

  const sortMark = (key) => {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const cards = [
    { label: 'Total Batches', value: viewKpis.total_batches, tone: 'slate' },
    { label: 'Currently Evaluating', value: viewKpis.currently_evaluating, tone: 'blue' },
    { label: 'Ready for EduPage', value: viewKpis.ready_for_edupage, tone: 'amber' },
    { label: 'Printed / Archived', value: viewKpis.printed_archived, tone: 'green' }
  ];
  const monthTitle = new Date(calendarMonth.year, calendarMonth.month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
  const cells = monthCells(calendarMonth.year, calendarMonth.month);
  const taskMonthTitle = new Date(taskMonth.year, taskMonth.month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
  const taskCells = monthCells(taskMonth.year, taskMonth.month);
  const rangeLo = rangeStart && rangeEnd ? (rangeStart < rangeEnd ? rangeStart : rangeEnd) : rangeStart;
  const rangeHi = rangeStart && rangeEnd ? (rangeStart < rangeEnd ? rangeEnd : rangeStart) : rangeStart;
  const selectionLabel = !rangeStart
    ? 'All loaded dates'
    : rangeEnd
      ? `${formatDateKey(rangeLo)} – ${formatDateKey(rangeHi)}`
      : formatDateKey(rangeStart);

  return (
    <div className="pd">
      <div className="pd-head">
        <div>
          <h1 className="pd-title">Pipeline Dashboard</h1>
          <p className="pd-subhead">
            {selectionLabel}
            {scope === 'live' ? ' · Live queue' : ' · All dates'}
            {generatedAt ? ` · Updated ${formatWhen(generatedAt)}` : ''}
          </p>
        </div>
        <div className="pd-head-actions">
          <div className="pd-scope" role="group" aria-label="Folder scope">
            <button
              type="button"
              className={scope === 'live' ? 'is-on' : ''}
              onClick={() => setScope('live')}
            >
              Live queue
            </button>
            <button
              type="button"
              className={scope === 'all' ? 'is-on' : ''}
              onClick={() => setScope('all')}
            >
              All dates
            </button>
          </div>
          <button type="button" className="pd-refresh" onClick={() => load(scope)}>
            Refresh
          </button>
        </div>
      </div>

      <div className="pd-kpis">
        {cards.map((card) => (
          <div key={card.label} className={`pd-kpi pd-kpi-${card.tone}`}>
            <span className="pd-kpi-value">{loading && !generatedAt ? '–' : card.value}</span>
            <span className="pd-kpi-label">{card.label}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="pd-error" role="alert">
          {error}
          <button type="button" onClick={() => load(scope)}>Try again</button>
        </div>
      )}

      <div className="pd-body">
        <aside className="pd-cal" aria-label="Date calendar">
          <div className="pd-cal-head">
            <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
            <span>{monthTitle}</span>
            <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
          </div>
          <p className="pd-cal-hint">Click a day, then a second day for the range.</p>
          <div className="pd-cal-pill">{selectionLabel}</div>
          <div className="pd-cal-week" aria-hidden="true">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => (
              <span key={`${label}-${index}`}>{label}</span>
            ))}
          </div>
          <div className="pd-cal-grid">
            {cells.map((cell) => {
              const count = countsByDate.get(cell.key) || 0;
              const isStart = cell.key === rangeStart;
              const isEnd = !!rangeEnd && cell.key === rangeEnd;
              const isEndpoint = isStart || isEnd;
              const inRange = !!(rangeLo && rangeHi && cell.key > rangeLo && cell.key < rangeHi);
              const isToday = cell.key === todayKey;
              return (
                <button
                  key={cell.key}
                  type="button"
                  className={[
                    'pd-day',
                    cell.inMonth ? '' : 'is-out',
                    isEndpoint ? 'is-end' : '',
                    inRange ? 'is-range' : '',
                    isToday && !isEndpoint ? 'is-today' : '',
                    count > 0 && !isEndpoint ? 'has-folders' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleDateClick(cell.key)}
                  aria-pressed={isEndpoint || inRange}
                  title={count > 0 ? `${count} folder${count === 1 ? '' : 's'}` : undefined}
                >
                  <span className="pd-day-num">{cell.day}</span>
                  {count > 0 && <span className="pd-day-count">{count}</span>}
                </button>
              );
            })}
          </div>
          {rangeStart && (
            <button
              type="button"
              className="pd-cal-clear"
              onClick={() => { setRangeStart(null); setRangeEnd(null); }}
            >
              Clear date
            </button>
          )}

          <section className="pd-tasks" aria-label="Tasks by day">
            <div className="pd-tasks-label">Tasks</div>
            <div className="pd-cal-head">
              <button type="button" onClick={() => shiftTaskMonth(-1)} aria-label="Previous task month">‹</button>
              <span>{taskMonthTitle}</span>
              <button type="button" onClick={() => shiftTaskMonth(1)} aria-label="Next task month">›</button>
            </div>
            <p className="pd-cal-hint">Work done on each day.</p>
            <div className="pd-cal-week" aria-hidden="true">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => (
                <span key={`task-${label}-${index}`}>{label}</span>
              ))}
            </div>
            <div className="pd-cal-grid">
              {taskCells.map((cell) => {
                const count = taskCounts.get(cell.key) || 0;
                const selected = cell.key === taskDay;
                const isToday = cell.key === todayKey;
                return (
                  <button
                    key={cell.key}
                    type="button"
                    className={[
                      'pd-day',
                      cell.inMonth ? '' : 'is-out',
                      selected ? 'is-end' : '',
                      isToday && !selected ? 'is-today' : '',
                      count > 0 && !selected ? 'has-folders' : ''
                    ].filter(Boolean).join(' ')}
                    onClick={() => setTaskDay(cell.key)}
                    aria-pressed={selected}
                    title={count > 0 ? `${count} task${count === 1 ? '' : 's'}` : undefined}
                  >
                    <span className="pd-day-num">{cell.day}</span>
                    {count > 0 && <span className="pd-day-count">{count}</span>}
                  </button>
                );
              })}
            </div>
          </section>
        </aside>

        <div className="pd-main">
      {!showTasks && (
      <>
      <div className="pd-filters">
        <button
          type="button"
          className="pd-table-toggle"
          onClick={() => setShowTasks(true)}
        >
          Show tasks
        </button>
        <input
          className="pd-search"
          type="search"
          value={search}
          placeholder="Search folder or batch"
          aria-label="Search folder or batch"
          onChange={(event) => setSearch(event.target.value)}
        />
        <select aria-label="Review status" value={review} onChange={(event) => setReview(event.target.value)}>
          <option value="all">Review: all</option>
          <option value="pending">Pending Review</option>
          <option value="reviewed">Reviewed</option>
        </select>
        <select aria-label="Medium" value={medium} onChange={(event) => setMedium(event.target.value)}>
          <option value="all">Medium: all</option>
          <option value="pending">Pending</option>
          <option value="english">English</option>
          <option value="urdu">Urdu</option>
          <option value="urog">UrOG</option>
        </select>
        <select aria-label="Queue or checking state" value={queue} onChange={(event) => setQueue(event.target.value)}>
          <option value="all">Queue: all</option>
          <option value="in_queue">In Queue</option>
          <option value="processing">Processing</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="not_started">Not started</option>
        </select>
        <select aria-label="EduPage sync status" value={edupage} onChange={(event) => setEdupage(event.target.value)}>
          <option value="all">EduPage: all</option>
          <option value="pending">Pending</option>
          <option value="ready">Ready for EduPage</option>
          <option value="synced">Synced to EduPage</option>
        </select>
        {sort.key !== 'queue' && (
          <button type="button" className="pd-refresh" onClick={() => setSort({ key: 'queue', dir: 'asc' })}>
            Queue order
          </button>
        )}
        <span className="pd-count">{filtered.length} of {dateRows.length}</span>
      </div>

      <div className="pd-scroll">
        <table className="pd-table">
          <thead>
            <tr>
              <th aria-sort={sort.key === 'name' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('name')}>Folder / Batch{sortMark('name')}</button>
              </th>
              <th aria-sort={sort.key === 'assignment' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('assignment')}>Assignment Names{sortMark('assignment')}</button>
              </th>
              <th aria-sort={sort.key === 'date' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('date')}>Date{sortMark('date')}</button>
              </th>
              <th aria-sort={sort.key === 'images' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('images')}>Images Ingested{sortMark('images')}</button>
              </th>
              <th aria-sort={sort.key === 'review' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('review')}>Review Status{sortMark('review')}</button>
              </th>
              <th aria-sort={sort.key === 'medium' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('medium')}>Medium{sortMark('medium')}</button>
              </th>
              <th aria-sort={sort.key === 'check_started' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('check_started')}>Check started{sortMark('check_started')}</button>
              </th>
              <th aria-sort={sort.key === 'regrade' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('regrade')}>Regrade started{sortMark('regrade')}</button>
              </th>
              <th aria-sort={sort.key === 'progress' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => toggleSort('progress')}>Checking Progress{sortMark('progress')}</button>
              </th>
              <th>Export &amp; Print</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="pd-empty">Loading pipeline…</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="pd-empty">
                  {rows.length === 0
                    ? 'No folders in this view.'
                    : dateRows.length === 0
                      ? 'No folders in this date selection.'
                      : 'No folders match these filters.'}
                </td>
              </tr>
            ) : filtered.map((row) => {
              const name = titleOf(row);
              const meta = [row.class, row.subject].filter(Boolean).join(' · ');
              return (
                <tr key={row.id}>
                  <td>
                    <button
                      type="button"
                      className="pd-name"
                      title={`Open ${name} on Check`}
                      onClick={() => openCheck(row)}
                    >
                      {name}
                    </button>
                    {meta && <div className="pd-meta">{meta}</div>}
                    {Array.isArray(row.flags) && row.flags.length > 0 && (
                      <div className="pd-flags" title={row.flag_note || undefined}>
                        {row.flags.map((flag) => (
                          <span key={flag} className={`pd-flag pd-flag-${flag}`}>
                            {FLAG_LABEL[flag] || flag}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="pd-assignment">{row.assignment_name || '—'}</td>
                  <td className="pd-date">{formatDateKey(dateKey(row.test_date))}</td>
                  <td>
                    <button
                      type="button"
                      className="pd-ingest"
                      title="Open this folder in Scans"
                      onClick={() => openScans(row)}
                    >
                      <span className={`pd-badge pd-ingest-${row.ingestion}`}>
                        {row.ingestion === 'empty' ? 'Empty' : 'Received'}
                      </span>
                      <span className="pd-sheets">
                        {row.scan_count} / {row.expected_count} sheets
                      </span>
                    </button>
                  </td>
                  <td>
                    <span className="pd-export-item">
                      <span className={`pd-badge pd-review-${row.review}`}>
                        {REVIEW_LABEL[row.review] || 'Pending Review'}
                      </span>
                      {row.review === 'reviewed' && formatWhen(row.reviewed_at) && (
                        <span className="pd-detail" title="When this list was marked reviewed">
                          {formatWhen(row.reviewed_at)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className={`pd-badge pd-medium-${row.medium}`}>
                      {MEDIUM_LABEL[row.medium] || 'Pending'}
                    </span>
                  </td>
                  <td>
                    <span className="pd-detail" title="When checking started">
                      {formatWhen(row.check_started_at) || '—'}
                    </span>
                  </td>
                  <td>
                    <div className="pd-times">
                      {Array.isArray(row.regrades) && row.regrades.length > 0
                        ? row.regrades.map((run, index) => (
                          <RegradeTime key={`${run.at}-${index}`} run={run} />
                        ))
                        : <span className="pd-detail">—</span>}
                    </div>
                  </td>
                  <td>
                    <div className="pd-progress">
                      <span className={`pd-badge pd-queue-${row.queue}`}>
                        {QUEUE_LABEL[row.queue] || 'Not started'}
                      </span>
                      {row.queue === 'completed' && formatWhen(row.completed_at) && (
                        <span className="pd-detail" title="When this check completed">
                          {formatWhen(row.completed_at)}
                        </span>
                      )}
                      {row.queue_detail && <span className="pd-detail">{row.queue_detail}</span>}
                      <div
                        className="pd-bar"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={row.progress_pct || 0}
                        aria-label={`${row.checked || 0} of ${row.total || 0} checked`}
                      >
                        <span style={{ width: `${row.progress_pct || 0}%` }} />
                      </div>
                      <span className="pd-progress-text">
                        {row.total > 0
                          ? `${row.checked}/${row.total} Checked • ${row.progress_pct}%`
                          : row.queue === 'completed'
                            ? 'Checked'
                            : 'No sorted list yet'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="pd-export">
                      <span className="pd-export-item">
                        <span className={`pd-badge pd-edu-${row.edupage}`}>
                          {EDU_LABEL[row.edupage] || 'Pending'}
                        </span>
                        {row.edupage !== 'pending' && formatWhen(row.edupage_at) && (
                          <span className="pd-detail" title={row.edupage === 'synced' ? 'When this was synced to EduPage' : 'When this was ready for EduPage'}>
                            {formatWhen(row.edupage_at)}
                          </span>
                        )}
                      </span>
                      {row.print && row.print !== 'none' && (
                        <span className="pd-export-item">
                          <span className={`pd-badge pd-print-${row.print}`}>
                            {PRINT_LABEL[row.print]}
                          </span>
                          {formatWhen(row.print_at) && (
                            <span
                              className="pd-detail"
                              title={row.print === 'printed' ? 'When this was printed' : 'When this was ready to print'}
                            >
                              {formatWhen(row.print_at)}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
      )}

      {showTasks && (
      <section className="pd-task-panel" aria-label="Tasks for the selected day">
        <div className="pd-task-bar">
          <button
            type="button"
            className="pd-table-toggle"
            onClick={() => setShowTasks(false)}
          >
            Hide tasks
          </button>
          <div className="pd-task-heading">
            <span className="pd-tasks-label">Tasks</span>
            <span className="pd-task-day">{formatDateKey(taskDay)}</span>
          </div>
          <div className="pd-task-events" role="tablist" aria-label="Event">
            {TASK_FILTERS.map((item) => (
              <button
                key={item.id || 'all'}
                type="button"
                role="tab"
                aria-selected={taskEvent === item.id}
                className={taskEvent === item.id ? 'is-on' : ''}
                onClick={() => setTaskEvent(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <input
            className="pd-search pd-task-search"
            type="search"
            value={taskQuery}
            placeholder="User, folder, or event"
            aria-label="Search tasks"
            onChange={(event) => setTaskQuery(event.target.value)}
          />
          {(taskQuery || taskEvent) && (
            <button
              type="button"
              className="pd-task-clear"
              onClick={() => { setTaskQuery(''); setTaskEvent(''); }}
            >
              Clear
            </button>
          )}
          <span className="pd-count">
            {shownTasks.length === dayTasks.length
              ? `${dayTasks.length} task${dayTasks.length === 1 ? '' : 's'}`
              : `${shownTasks.length} of ${dayTasks.length}`}
          </span>
        </div>
        <div className="pd-task-scroll">
          <table className="pd-task-table">
            <thead>
              <tr>
                <th aria-sort={taskSort.key === 'time' ? (taskSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleTaskSort('time')}>Time{taskSortMark('time')}</button>
                </th>
                <th aria-sort={taskSort.key === 'user' ? (taskSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleTaskSort('user')}>User{taskSortMark('user')}</button>
                </th>
                <th aria-sort={taskSort.key === 'event' ? (taskSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleTaskSort('event')}>Event{taskSortMark('event')}</button>
                </th>
                <th aria-sort={taskSort.key === 'folder' ? (taskSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleTaskSort('folder')}>Folder{taskSortMark('folder')}</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {shownTasks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="pd-empty">
                    {dayTasks.length === 0 ? 'No tasks on this day.' : 'No tasks match this view.'}
                  </td>
                </tr>
              ) : shownTasks.map((task) => (
                <tr key={task.key}>
                  <td className="pd-task-time">{formatTime(task.at)}</td>
                  <td className="pd-task-user">{task.user || '—'}</td>
                  <td>
                    <span className={`pd-task-kind pd-task-${task.kind}`}>{TASK_LABEL[task.kind]}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="pd-task-folder"
                      title={`Open ${task.folder} on Check`}
                      onClick={() => openCheck(task.row)}
                    >
                      {task.folder}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}
        </div>
      </div>
    </div>
  );
}
